import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { fetchDeclineSuggestionOptions } from '../../util/api';
import {
  DECLINE_SUGGESTIONS_MAX,
  previewSuggestionOptions,
} from '../../util/declineSuggestions';

import css from './DeclineSuggestTimes.module.css';

const optionsFromPayload = payload => {
  const data = payload?.data?.data || payload?.data || payload || {};
  return {
    options: Array.isArray(data.options) ? data.options : [],
    timeZone: typeof data.timeZone === 'string' ? data.timeZone : undefined,
  };
};

/**
 * Listener decline: optionally pick 1-3 of their open 15-minute times.
 * The parent saves the picks right before the normal decline runs.
 *
 * @param {Object} props
 * @param {string} props.transactionId
 * @param {Function} props.onChange called with ISO start strings
 * @param {boolean} [props.preview] sample times, no API (screenshots)
 * @param {string} [props.talkerName]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const DeclineSuggestTimes = props => {
  const { transactionId, onChange, preview = false, talkerName, className } = props;
  const intl = useIntl();
  const [open, setOpen] = useState(!!preview);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [options, setOptions] = useState([]);
  const [timeZone, setTimeZone] = useState(undefined);
  const [day, setDay] = useState(null);
  const [picked, setPicked] = useState([]);

  useEffect(() => {
    if (!open || options.length > 0 || loading) {
      return undefined;
    }
    if (preview) {
      setOptions(previewSuggestionOptions());
      return undefined;
    }
    if (!transactionId) {
      setFailed(true);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setFailed(false);
    fetchDeclineSuggestionOptions(transactionId)
      .then(payload => {
        if (!alive) {
          return;
        }
        const next = optionsFromPayload(payload);
        setOptions(next.options);
        setTimeZone(next.timeZone);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transactionId, preview]);

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange(open ? picked : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, picked]);

  const days = useMemo(() => {
    const keys = [];
    options.forEach(o => {
      if (o.day && !keys.includes(o.day)) {
        keys.push(o.day);
      }
    });
    return keys;
  }, [options]);
  const activeDay = day && days.includes(day) ? day : days[0] || null;
  const times = options.filter(o => o.day === activeDay);

  const dayLabel = key => {
    const sample = options.find(o => o.day === key);
    return sample
      ? intl.formatDate(new Date(sample.start), {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone,
        })
      : key;
  };
  const timeLabel = iso =>
    intl.formatDate(new Date(iso), { hour: 'numeric', minute: '2-digit', timeZone });
  const fullLabel = iso =>
    intl.formatDate(new Date(iso), {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone,
    });

  const toggle = iso => {
    setPicked(prev => {
      if (prev.includes(iso)) {
        return prev.filter(p => p !== iso);
      }
      if (prev.length >= DECLINE_SUGGESTIONS_MAX) {
        return prev;
      }
      return [...prev, iso].sort();
    });
  };

  return (
    <div
      className={classNames(css.root, className)}
      data-decline-suggest="true"
      data-decline-suggest-open={open ? 'true' : 'false'}
    >
      <label className={css.toggle}>
        <input
          type="checkbox"
          checked={open}
          onChange={e => setOpen(e.target.checked)}
          data-decline-suggest-toggle="true"
        />
        <span className={css.toggleText}>
          <strong>
            <FormattedMessage id="DeclineSuggestTimes.toggle" />
          </strong>
          <span className={css.toggleHint}>
            <FormattedMessage
              id="DeclineSuggestTimes.hint"
              values={{ name: talkerName || intl.formatMessage({ id: 'DeclineSuggestTimes.theTalker' }) }}
            />
          </span>
        </span>
      </label>

      {open ? (
        <div className={css.panel}>
          {loading ? (
            <p className={css.note}>
              <FormattedMessage id="DeclineSuggestTimes.loading" />
            </p>
          ) : failed ? (
            <p className={css.note} role="alert">
              <FormattedMessage id="DeclineSuggestTimes.failed" />
            </p>
          ) : options.length === 0 ? (
            <p className={css.note}>
              <FormattedMessage id="DeclineSuggestTimes.none" />
            </p>
          ) : (
            <>
              <p className={css.pickLabel} id="decline-suggest-label">
                <FormattedMessage
                  id="DeclineSuggestTimes.pickLabel"
                  values={{ count: picked.length, max: DECLINE_SUGGESTIONS_MAX }}
                />
              </p>
              <div className={css.days} role="tablist">
                {days.map(key => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={key === activeDay}
                    className={classNames(css.dayChip, key === activeDay ? css.dayChipActive : null)}
                    onClick={() => setDay(key)}
                  >
                    {dayLabel(key)}
                  </button>
                ))}
              </div>
              <div className={css.times} role="group" aria-labelledby="decline-suggest-label">
                {times.map(o => {
                  const selected = picked.includes(o.start);
                  const full = !selected && picked.length >= DECLINE_SUGGESTIONS_MAX;
                  return (
                    <button
                      key={o.start}
                      type="button"
                      aria-pressed={selected}
                      disabled={full}
                      className={classNames(css.timeChip, selected ? css.timeChipSelected : null)}
                      data-decline-suggest-time={o.start}
                      onClick={() => toggle(o.start)}
                    >
                      {timeLabel(o.start)}
                    </button>
                  );
                })}
              </div>
              {picked.length > 0 ? (
                <ul className={css.picked} data-decline-suggest-picked={picked.length}>
                  {picked.map(iso => (
                    <li key={iso}>
                      <span className={css.dot} aria-hidden="true" />
                      <span>{fullLabel(iso)}</span>
                      <button
                        type="button"
                        className={css.remove}
                        onClick={() => toggle(iso)}
                        aria-label={intl.formatMessage({ id: 'DeclineSuggestTimes.remove' })}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default DeclineSuggestTimes;
